"use client";

import React from "react";
import { ArrowRight, Activity, Flame, Zap, Compass, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface ServiceItem {
  id: string;
  name: string;
  price: string;
  description: string;
  note?: string;
  link?: string;
  linkText?: string;
  isFoundational?: boolean;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const services: ServiceItem[] = [{
  id: "vo2-max",
  name: "VO₂ Max Assessment",
  price: "£160",
  description: "Objective aerobic-fitness and exercise-response testing.",
  icon: Zap
}, {
  id: "rmr",
  name: "Resting Metabolic Rate",
  price: "£90",
  description: "Measured resting energy expenditure.",
  icon: Flame
}, {
  id: "complete-metabolic",
  name: "Complete Metabolic & Aerobic Assessment",
  price: "£220",
  description: "RMR + VO₂ Max.",
  icon: Activity
}, {
  id: "gait-balance",
  name: "Gait & Balance Assessment",
  price: "From existing pricing",
  description: "From the current Gait & Balance Assessment pricing.",
  link: "/GaitBalanceAssessmentPage",
  linkText: "View Gait & Balance details",
  icon: Compass
}, {
  id: "physiotherapy",
  name: "Physiotherapy",
  price: "Clinical assessment",
  description: "Available where pain, injury or clinical assessment is required.",
  link: "/ExpertPhysiotherapyPage",
  linkText: "Explore Expert Physiotherapy",
  isFoundational: true,
  note: "Foundational PhysAnalytica discipline",
  icon: Stethoscope
}];
export default function AssessmentPathway() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="assessment-pathway" data-nav="light" ref={ref} className="py-16 md:py-24 bg-background text-foreground transition-opacity duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
            Build your assessment around what you want to understand
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.id} data-index={i} className={`relative flex flex-col justify-between border transition-all duration-200 hover:border-primary/40 ${item.isFoundational ? "border-primary/30 bg-card md:col-span-2 lg:col-span-1 shadow-sm" : "border-border bg-card shadow-2xs"}`}>
                <div>
                  <CardHeader className="gap-3 pb-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="p-2 rounded-md bg-secondary text-primary inline-flex">
                        <Icon className="size-5" />
                      </div>
                      {item.isFoundational && item.note && <span className="text-xs font-medium text-primary px-2.5 py-1 rounded-full bg-primary/10">
                          {item.note}
                        </span>}
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground pt-1">
                      {item.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="text-2xl font-bold tracking-tight text-foreground">
                      {item.price}
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </div>

                {item.link && <div className="px-6 pb-6 pt-2">
                    <Link to={item.link} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                      <span>{item.linkText}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </div>}
              </Card>;
        })}
        </div>

        <div className="mt-12 md:mt-16 p-6 sm:p-8 rounded-xl border border-border bg-secondary/40 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <p className="text-sm sm:text-base text-secondary-foreground leading-relaxed max-w-2xl">
            For clients wanting a broader longevity and fitness assessment, PhysAnalytica can recommend an appropriate combination based on goals and current physical activity.
          </p>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/BookNowPage">
              <span>Discuss a Longevity &amp; Fitness Assessment</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}